<template>
<div>
  <h1 class="csl_header">Concurrent stream limiting & stream takedown</h1>
  <div class="players_container">
    <div v-for="n in numOfPlayers" :key="n">
      <DemoPageCslPlayer
        ref='players'
        :asset='asset'
        :loadDelay='(n - 1) * 7500'
        v-on:loaded='playerLoaded()'
        v-on:blocked='playerBlocked(n)'
        :willBlock='nextBlockedPlayer === n'
        :expirationDate.sync='expirationDates[n - 1]'
        :nextPlayerExpirationDate='expirationDates[n % numOfPlayers]'
      />
    </div>
  </div>

  <div>
    <div class="button_group">
      <button class="button primary" @click="takedown()">Takedown</button>
      <button class="button" @click="reset()">Reset</button>
    </div>
    <div class="csl_header stream_status">
      <h2 v-if="takenDown">Stream has been taken down.</h2>
      <h2 v-else>Stream is currently available.</h2>
    </div>
    <div class='description'>
      <p>
        Concurrent Stream Limiting allows streaming service providers to control access
        to a stream by denying content license requests. This helps to control content
        access as a stream will not play for a user if they are denied a license.
      </p>
      <p>
        Concurrent Stream Limiting can be used to control access to a given user by:
      </p>
      <ul>
        <li>Preventing too many different content streams from being accessed simultaneously (enabling a ‘limited device number’ business model)</li>
        <li>Preventing multiple streams of the same content from being accessed simultaneously</li>
      </ul>
      <p>In this demonstration:</p>
      <ul>
        <li>The user has a concurrent stream limit of <b>three</b>.</li>
        <li>Four streams are continually trying to play with each requesting a Widevine license renewal every 30 seconds to verify playback can continue.</li>
        <li>When a fourth stream begins to play, then the next license renewal request will be denied, and that stream will not be able to continue.</li>
      </ul>
    </div>
  </div>
</div>
</template>

<script>
import DemoPageCslPlayer from '../components/DemoPageCslPlayer.vue';

const numOfPlayers = 4;
const cslTrackingId = 'e86482eb-8899-4487-9e50-111111111112';
const baseUrl = 'https://fe.staging.drmtoday.com/frontend/apis/csl/v1';
const merchantId = 'f7b7e4de-c73e-40f2-a206-26417caf6dd3';
const apiToken = '285fbcdf-4dda-408d-9a33-4bdb8d5acaaa';

const asset = {
  name: 'CSL',
  src: 'https://castlabs-dl.s3.amazonaws.com/public/Transcodes/OUT/PRESTODEMO/DASH/Manifest.mpd',
  cfg: {
    autoplay: true,
    muted: true,
    loop: true,
    startTime: 3,
    drm: {
      env: 'DRMtoday_STAGING',
      preferredDrmSystem: 'com.widevine.alpha',
      customData: {
        assetId: 'PRESTO_CENC',
        userId: 'purchase',
        sessionId:
          `crtjson: ${JSON.stringify({
            profile: {
              purchase: {}
            },
            ref: '6466ba37-27b9-4255-972a-293c97705a09',
            csl: {
              cslTrackingId,
              sessionDuration: 720000,
              derivationType: 'CLIENT_ASSET'
            },
            storeLicense: false
          })}`,
        merchant: 'castlabsmarketing'
      }
    },
    restrictions: {
      maxWidth: 1000
    }
  }
};

const getUrl_ = (command) => {
  return [baseUrl, apiToken, merchantId, command].join('/');
};

const makeRequest_ = async (command, cslTrackingId) => {
  const url = getUrl_(command);
  const body = cslTrackingId ? JSON.stringify({ tri: cslTrackingId }) : null;
  let result = null;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body
    });
    if (response.ok) {
      result = await response.json();
    } else {
      console.error(`Network response: ${response.statusText}`);
    }
  } catch (e) {
    console.error('Error during fetch', e);
  }

  return result;
};

export default {
  name: 'PageDemoCsl',
  data () {
    return {
      numOfPlayers,
      asset,
      expirationDates: Array(numOfPlayers).fill(0),
      loadedPlayers: 0,
      blockedPlayer: numOfPlayers,
      nextBlockedPlayer: -1,
      takenDown: false,
      timer: null
    };
  },
  mounted () {
    this.timer = setInterval(this.get, 5000);
  },
  beforeDestroy () {
    clearInterval(this.timer);
  },
  methods: {
    playerLoaded () {
      this.loadedPlayers = Math.min(this.loadedPlayers + 1, this.numOfPlayers);
      if (this.loadedPlayers === this.numOfPlayers) {
        this.nextBlockedPlayer = this.blockedPlayer === this.numOfPlayers ? 1 : this.blockedPlayer + 1;
      }
    },
    playerBlocked (player) {
      this.loadedPlayers = Math.max(this.loadedPlayers - 1, 0);
      this.blockedPlayer = player;
      this.nextBlockedPlayer = -1;
    },
    async takedown () {
      await makeRequest_('take-down', cslTrackingId);
    },
    async reset () {
      await makeRequest_('reset-match', cslTrackingId);
    },
    async get () {
      const response = await makeRequest_('get', cslTrackingId);
      const takenDown = !!response && response.tri === cslTrackingId;
      if (takenDown !== this.takenDown) {
        this.takenDown = takenDown;
        if (!takenDown) {
          // reload players to avoid timing issues
          this.blockedPlayer = numOfPlayers;
          this.nextBlockedPlayer = -1;
          for (const player of this.$refs.players) {
            player.reinit();
          }
        }
      }
    }
  },
  components: {
    DemoPageCslPlayer
  }
};
</script>

<style lang="css" scoped>
.players_container {
  display: grid;
  gap: 10px;
  margin: 6px;
  grid-template-columns: 500px 500px;
  grid-auto-rows: 400px;
  place-items: stretch;
}

.button_group {
  width: 100%;
  display: flex;
  justify-content: center;
}

.description {
  padding-left: 20px;
}

.csl_header {
  width: 100%;
  text-align: center;
  padding: 5px;
}

.stream_status {
  font-weight: bold;
}

</style>
